import * as THREE from 'three';
import { BaseModule } from '../base/BaseModule';
import { CSG } from 'three-csg-ts';

export class WardrobeModule extends BaseModule {
  constructor(
    moduleData,
    moduleType, // "base" | "tall" | "topbox"
    sceneManager,
    cabinet = null
  ) {
    super(moduleData, moduleType, sceneManager);

    // Wardrobe-specific properties
    this.cabinetType = moduleType; // base, tall, topbox
    this.doorType = moduleData.doorType || 'swing'; // swing | slide
    this.handleType = moduleData.handleType || 'standard';
    this.material = moduleData.material || 'wood';
    this.color = moduleData.color || '#ffffff';
    this.cabinet = cabinet; // reference đến parent cabinet nếu có

    // Ràng buộc cho tủ áo
    this.constraints = this.getConstraints();
  }

  getConstraints() {
    // Các giá trị (mm) – đồng nhất với KitchenModule; world unit quy đổi /1000 ở chỗ đặt vị trí
    return {
      // Khoảng hở tiêu chuẩn tủ áo
      toeKick: 80, // chân đế (plinth) 80mm
      crownGap: 15, // hở trần để lùa lắp dựng
      topboxGapAboveDoor: 0,

      // Thứ tự ưu tiên khi auto-place (nếu dùng)
      typePriorities: {
        base: 1,
        tall: 2,
        topbox: 3,
      },

      // Chiều cao danh định (mm) – sẽ còn tuỳ biến qua formular & fitCeiling
      typeHeights: {
        base: 900, // tủ thấp (dresser/console) – nếu dùng
        tall: 2200, // tủ áo đứng phổ biến
        topbox: 400, // tủ áp trần (đặt trên tủ tall)
      },
    };
  }

  /**
   * Override load để tính vị trí theo logic tủ áo
   */
  async load(yPosition) {
    await super.load();

    const calculatedTransform = this.calculateWardrobePosition(yPosition);
    this.setPosition(calculatedTransform.position);
    this.setRotation(calculatedTransform.rotation);

    return this.group;
  }

  /**
   * Scale theo formular; đồng thời dọn các “phần tử động” đặc thù wardrobe trước khi re-scale
   * (tương tự KitchenModule đã remove COUNTERTOP/BACKSPLASH)
   */
  scaleModuleWithFormular(mdDimensions = {}, mdOptions = {}) {
    // Dọn các phần tử động thường sinh ra theo preset (tuỳ dự án đặt tên)
    const removableNames = [
      'WARDROBE_PLINTH', // chân đế
      'WARDROBE_CROWN', // phào/trần
      'DYNAMIC_HANGER_BAR', // thanh treo
      'DYNAMIC_SHELF', // đợt động
      'DYNAMIC_DIVIDER', // vách chia động
    ];

    const lstRemoveScenes = this.group.children.filter(
      child => child.userData && removableNames.includes(child.userData.name)
    );

    lstRemoveScenes.forEach(scene => {
      this.group.remove(scene);
      if (scene.geometry) scene.geometry.dispose();
      if (scene.material) {
        if (Array.isArray(scene.material))
          scene.material.forEach(m => m.dispose());
        else scene.material.dispose();
      }
    });

    // // Nếu chọn đụng trần, ép chiều cao module bằng chiều cao phòng trừ toeKick & crownGap
    // // sceneManager.wallHeight là (m) theo cách KitchenModule dùng -> cần *1000 về mm
    // if (this.fitCeiling) {
    //   const roomHeightMM = (this.sceneManager.wallHeight || 2.7) * 1000; // fallback 2.7m
    //   const { toeKick, crownGap } = this.constraints;
    //   const targetHeightMM = Math.max(300, roomHeightMM - toeKick - crownGap); // bảo vệ min
    //   mdDimensions = {
    //     ...mdDimensions,
    //     height: targetHeightMM / 1000, // mm
    //   };
    // }

    super.scaleModuleWithFormular(mdDimensions, mdOptions);
  }

  /**
   * Tính vị trí đặt tủ áo theo loại:
   * - base: đặt trên sàn (có toeKick)
   * - tall: đặt từ sàn lên (toeKick), cao theo typeHeights hoặc fitCeiling
   * - topbox: đặt trên đỉnh tủ tall (hoặc dùng rule gap), thường cao 300–450mm
   *
   * Chú ý: world unit của scene là mét -> chia cho 1000 khi set vào Three.Vector3
   */
  calculateWardrobePosition(yPosition) {
    // const roomHeightMM = (this.sceneManager.wallHeight || 2.7) * 1000;
    // const { toeKick, crownGap, typeHeights } = this.constraints;

    // let yMM = 0;

    // switch (this.cabinetType) {
    //   case "base": {
    //     // Tủ thấp đặt trên sàn (có toeKick)
    //     yMM = toeKick; // tâm khối sẽ còn phụ thuộc điểm pivot; giữ tương đồng KitchenModule: set vị trí theo Y offset
    //     break;
    //   }
    //   case "tall": {
    //     // Tủ đứng: đặt từ sàn
    //     // Nếu fitCeiling: chiều cao đã ép ở scaleModuleWithFormular; vị trí đáy trên sàn = toeKick
    //     yMM = toeKick;
    //     break;
    //   }
    //   case "topbox": {
    //     // Topbox đặt trên nóc tủ tall. Ở đây, nếu không track chiều cao tủ tall runtime,
    //     // ta dùng quy ước: nóc tủ tall ≈ min(typeHeights.tall, roomHeight - crownGap) + toeKick
    //     const tallNominal = Math.min(typeHeights.tall, roomHeightMM - crownGap - toeKick);
    //     yMM = toeKick + tallNominal; // đặt lên trên nóc tủ tall
    //     break;
    //   }
    //   default: {
    //     // Fallback như tall
    //     yMM = toeKick;
    //   }
    // }

    // Tìm X, Z theo rule va chạm/edge giống KitchenModule
    const { position, rotation } = this.findOptimalPosition(yPosition);

    return {
      position: new THREE.Vector3(position.x, yPosition, position.z),
      rotation,
    };
  }

  /**
   * Hành vi cửa – placeholder cho animation
   */
  openDoor() {
    if (this.doorType === 'swing') {
      // TODO: animate xoay bản lề
    } else if (this.doorType === 'slide') {
      // TODO: animate trượt cánh
    }
  }

  closeDoor() {
    if (this.doorType === 'swing') {
      // TODO: animate đóng cánh
    } else if (this.doorType === 'slide') {
      // TODO: animate trượt về vị trí đóng
    }
  }

  /**
   * Đổi vật liệu – tương tự KitchenModule
   */
  changeMaterial(material) {
    this.material = material;
    if (this.group) {
      // TODO: duyệt mesh, thay material theo rule userData/tag
    }
  }

  /**
   * Serialize data – thêm wardrobe properties
   */
  getData() {
    return {
      ...super.getData(),
      doorType: this.doorType,
      handleType: this.handleType,
      material: this.material,
      color: this.color,
      fitCeiling: this.fitCeiling,
      cabinetType: this.cabinetType,
    };
  }
}
